import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ServerTable } from 'shared/ui/ServerTable';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as EditIcon } from 'shared/assets/icons/EditIcon.svg';
import { ReactComponent as DeleteIcon } from 'shared/assets/icons/DeleteIcon.svg';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { Toggle } from 'shared/ui/Toggle';
import { Paragraph } from 'shared/ui/Paragraph';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { showNotification } from 'app/providers/NotificationsProvider';
import classNames from 'classnames';
import { getLoggedUserData, getUserName, UserPermissions } from 'entities/User';
import { WaitingListFilter } from './WaitingListFilter';
import { useTableFilterContext } from 'features/TableFilter';
import { transformWaitingListFilters } from '../utils/transformNotesListFilters';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import {
  useDeleteWaitingListEntryMutation,
  useGetPaginatedWaitingListEntriesQuery,
  useUpdateWaitingListEntryMutation,
  WaitingListEntry,
} from 'entities/WaitingListEntry';
import { DrawerMode, waitingListEntryDrawerActions } from 'widgets/WaitingListEntryDrawer';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';

interface WaitingListTableProps {
  isCompleted: boolean;
}

export const WaitingListTable: FC<WaitingListTableProps> = memo((props) => {
  const { isCompleted } = props;

  const [selectedWaitingListEntry, setSelectedNote] = useState<Nullable<WaitingListEntry>>(null);
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const { t } = useAppTranslation(['contracts', 'common']);
  const dispatch = useAppDispatch();

  const loggedUser = useAppSelector(getLoggedUserData);

  const { getEncodedString } = useQueryString();

  const isForbiddenDelete = useCheckPermission(UserPermissions.DELETE_UNITS);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const { data: warehouses } = useGetAllWarehousesQuery();
  const [deleteWaitingListEntry, { isLoading: isDeletionLoading }] = useDeleteWaitingListEntryMutation();
  const [updateWaitingListEntry] = useUpdateWaitingListEntryMutation();

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
    setSelectedNote(null);
  }, []);

  const handleDeleteNote = useCallback(async (): Promise<void> => {
    try {
      if (selectedWaitingListEntry) {
        await deleteWaitingListEntry(selectedWaitingListEntry.waitingListEntryId).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Waiting list entry deleted successfully'));
      }
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when deleting waiting list entry'));
      console.log('error', error);
    } finally {
      closeConfirmModal();
    }
  }, [selectedWaitingListEntry, deleteWaitingListEntry, t, closeConfirmModal]);

  const createNote = useCallback((): void => {
    dispatch(waitingListEntryDrawerActions.setOpenWaitingListEntryDrawer({ mode: DrawerMode.CREATE }));
  }, [dispatch]);

  const editNote = useCallback(
    (waitingListEntry: WaitingListEntry): void => {
      dispatch(waitingListEntryDrawerActions.setOpenWaitingListEntryDrawer({ mode: DrawerMode.EDIT, waitingListEntry }));
    },
    [dispatch],
  );

  const toggleNoteStatus = useCallback(
    async (waitingListEntry: WaitingListEntry) => {
      try {
        await updateWaitingListEntry({
          waitingListEntryId: waitingListEntry.waitingListEntryId,
          isCompleted: !waitingListEntry.isCompleted,
          description: `${t('Waiting list entry status changed by')} ${getUserName(loggedUser)}`,
        }).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Waiting list entry status changed successfully'));
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when changing waiting list entry status'));
        console.log('error', error);
      }
    },
    [loggedUser, t, updateWaitingListEntry],
  );

  const tableActions: Array<ServerTableActions<WaitingListEntry>> = useMemo(
    () => [
      {
        name: t('Add to waiting list'),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: createNote,
      },
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [createNote, setFiltersOpened, t],
  );

  const rowActions: Array<ServerTableRowActions<WaitingListEntry>> = useMemo(
    () => [
      {
        name: 'edit',
        icon: <EditIcon />,
        theme: 'clear',
        description: t('Edit'),

        onClick: editNote,
      },
      {
        name: 'delete',
        icon: <DeleteIcon />,
        theme: 'clear',
        description: t('Delete'),
        onClick: (selectedWaitingListEntry: WaitingListEntry) => {
          setSelectedNote(selectedWaitingListEntry);
          openConfirmModal();
        },
      },
    ],
    [editNote, openConfirmModal, t],
  );

  const columns = useMemo<ColumnsType<WaitingListEntry>>(
    () => [
      {
        title: t('Created by'),
        key: 'createdBy',
        render: (_, record) => {
          return (
            <div className="text-primary font-normal">
              {record.createdBy?.firstName} {record.createdBy?.lastName}
            </div>
          );
        },
      },
      {
        title: t('Warehouse'),
        key: 'warehouse',
        render: (_, record) => {
          const assignedWarehouses = warehouses?.filter((warehouse) => record.warehouseIds?.includes(warehouse.warehouseId));

          if (!assignedWarehouses?.length) {
            return <div>{t('Not specified')}</div>;
          }

          return (
            <div className="text-primary font-normal">
              {assignedWarehouses?.map((warehouse) => {
                return <div key={warehouse.warehouseId}>{warehouse.name}</div>;
              })}
            </div>
          );
        },
      },
      {
        title: t('User'),
        key: 'email',
        render: (_, record) => {
          const searchValue = record.lead ? record.lead?.email || record.lead?.phone : record.user?.email;

          const tabValue = record.lead ? 'lead' : 'customer';

          const tabLabel = record.lead ? t('Leads') : t('Customers');

          const encodedFilters = getEncodedString({
            search: { value: searchValue || '', label: searchValue || '', title: t('Search') },
            tab: { value: tabValue, label: tabLabel, title: t('Tab') },
          });

          const userName = record.lead
            ? `${record.lead.firstName || ''} ${record.lead.lastName || ''}`
            : record.user
            ? getUserName(record.user)
            : '';

          return (
            <Link
              to={`${AppRoutes.USERS}?tab=${encodedFilters.tab}&search=${encodedFilters.search}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col"
            >
              <div className="text-sm text-accent cursor-pointer underline"> {searchValue}</div>
              <div className="font-bold text-accent cursor-pointer underline">{userName}</div>
            </Link>
          );
        },
      },
      {
        title: t('Unit'),
        key: 'preferredDate',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div className=" text-primary font-normal">
            <div className="flex gap-1">
              <span>{t('Unit type')}:</span>
              <span className="font-semibold">{t(record.unitType)}</span>
            </div>
            {(record.squareFrom || record.squareTo) && (
              <div className="flex gap-1">
                <span>{t('Size')}:</span>
                <span className="font-semibold">
                  {record.squareFrom === record.squareTo && record.squareFrom}

                  {record.squareFrom !== record.squareTo && (
                    <>
                      {record.squareFrom && (
                        <span className="font-semibold">
                          {t('From')} {record.squareFrom}
                        </span>
                      )}

                      {record.squareFrom && record.squareTo && ' '}

                      {record.squareTo && (
                        <span className="font-semibold">
                          {t('To')} {record.squareTo}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
            )}
            <div className="flex gap-1">
              <span>{t('Preferred date')}:</span>
              <span className="font-semibold">
                {record?.preferredDate ? getClientDateFormat(record.preferredDate) : t('Not specified')}
              </span>
            </div>
            <div></div>
          </div>
        ),
      },
      {
        title: t('Status'),
        key: 'isCompleted',
        render: (_, record) => (
          <div>
            <Toggle
              checked={record.isCompleted}
              onChange={async () => {
                await toggleNoteStatus(record);
              }}
            />
            <div className="text-xs text-primaryLight mt-2 ">{t(record.isCompleted ? 'Completed' : 'Incompleted')}</div>
          </div>
        ),
      },
      {
        title: t('Description'),
        key: 'description',
        render: (_, record) => {
          const paragraphs = record.description?.split('\n');

          const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
            <p key={index} className="font-normal !mb-0">
              {paragraph}
            </p>
          ));

          return <Paragraph rows={4}>{paragraphElements}</Paragraph>;
        },
      },
    ],
    [t, warehouses, getEncodedString, toggleNoteStatus],
  );

  const highlightRow = (record: WaitingListEntry): string => {
    return classNames({
      'bg-successLight': record.isCompleted,
      'bg-warnLight': !record.isCompleted,
    });
  };

  return (
    <div className="space-y-4">
      <ServerTable
        fetchData={useGetPaginatedWaitingListEntriesQuery}
        columns={columns}
        rowKey="noteId"
        title={t(isCompleted ? 'Completed waiting list' : 'Waiting list')}
        tableActions={tableActions}
        tableActionsPosition="prev"
        rowActions={rowActions}
        rowClassName={highlightRow}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        search
        defaultFilters={{
          isCompleted,
          ...transformWaitingListFilters(appliedFilters),
        }}
      />
      <ConfirmModal
        isOpened={isOpenedConfirm}
        title={t('Delete')}
        description={t('Are you sure you want to delete this position?')}
        isLoading={isDeletionLoading}
        onOk={handleDeleteNote}
        onCancel={closeConfirmModal}
        isConfirmDisabled={isForbiddenDelete}
      />
      <WaitingListFilter />
    </div>
  );
});
